import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, CreditCard, Wrench, AlertCircle, ArrowRight, Loader2, FileSignature, FolderOpen } from "lucide-react";

const statusColors = {
  submitted: "bg-blue-50 text-blue-700",
  under_review: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  completed: "bg-emerald-50 text-emerald-700",
  awaiting_payment: "bg-amber-50 text-amber-700",
  documents_pending: "bg-orange-50 text-orange-700",
};

export default function TenantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tenant/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  const app = data?.application;
  const payments = data?.payments || {};
  const maintenance = data?.maintenance || {};

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8" data-testid="tenant-dashboard">
        {/* Welcome */}
        <div className="bg-[#0A2540] text-white rounded-lg p-6 sm:p-8" data-testid="welcome-banner">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Welcome back, {user?.name}</h2>
          <p className="text-white/60 mt-1 text-sm">Here's an overview of your accommodation status.</p>
        </div>

        {/* Action Items */}
        {!app && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="no-application-alert">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">No active application</p>
              <p className="text-xs text-amber-600 mt-0.5">Browse rooms and submit an application to get started.</p>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/rooms")} data-testid="browse-rooms-btn">
              Browse Rooms
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-border/60" data-testid="application-status-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Application</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              {app ? (
                <>
                  <Badge className={statusColors[app.status] || "bg-gray-100 text-gray-700"}>{app.status?.replace(/_/g, " ")}</Badge>
                  <p className="text-xs text-muted-foreground mt-2">Room {app.room_number}</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No application</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60" data-testid="lease-status-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Lease</CardTitle>
                <FileSignature className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              {data?.leases?.active ? (
                <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>
              ) : data?.leases?.pending_signature ? (
                <Badge className="bg-amber-50 text-amber-700">Needs Signatures</Badge>
              ) : (
                <p className="text-sm text-muted-foreground">No lease</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60" data-testid="documents-status-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Documents</CardTitle>
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-2xl font-bold text-[#0A2540]">{data?.documents?.approved || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {(data?.documents?.pending || 0) > 0 ? `${data.documents.pending} pending` : "approved"}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/60" data-testid="payment-summary-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Payments</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-2xl font-bold text-[#0A2540]">${payments.total_paid || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {payments.pending > 0 ? `$${payments.pending} pending` : "No pending payments"}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/60" data-testid="maintenance-summary-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Maintenance</CardTitle>
                <Wrench className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-2xl font-bold text-[#0A2540]">{maintenance.open_tickets || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Open tickets</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => navigate("/tenant/apply")} data-testid="quick-apply-btn">
            <FileText className="h-5 w-5 mr-3 text-[#E25822]" />
            <div className="text-left">
              <div className="font-medium text-sm">Application</div>
              <div className="text-xs text-muted-foreground">View or submit</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </Button>
          <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => navigate("/tenant/payments")} data-testid="quick-payments-btn">
            <CreditCard className="h-5 w-5 mr-3 text-[#E25822]" />
            <div className="text-left">
              <div className="font-medium text-sm">Payments</div>
              <div className="text-xs text-muted-foreground">View history</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </Button>
          <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => navigate("/tenant/maintenance")} data-testid="quick-maintenance-btn">
            <Wrench className="h-5 w-5 mr-3 text-[#E25822]" />
            <div className="text-left">
              <div className="font-medium text-sm">Maintenance</div>
              <div className="text-xs text-muted-foreground">Submit request</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </Button>
          <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => navigate("/tenant/leases")} data-testid="quick-leases-btn">
            <FileSignature className="h-5 w-5 mr-3 text-[#E25822]" />
            <div className="text-left">
              <div className="font-medium text-sm">Leases</div>
              <div className="text-xs text-muted-foreground">View & sign</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </Button>
          <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => navigate("/tenant/documents")} data-testid="quick-documents-btn">
            <FolderOpen className="h-5 w-5 mr-3 text-[#E25822]" />
            <div className="text-left">
              <div className="font-medium text-sm">Documents</div>
              <div className="text-xs text-muted-foreground">Upload KYC</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
